import React, { useRef, useState } from 'react';
import { Document } from '@spilca/accounting_ts/v1/auth_common/dtos_pb';
import { QUERY_KEYS } from '@monorepo/helpers';
import { TGRPCPromiseReject } from '@monorepo/grpc';
import { useNotifications } from '@monorepo/notification';
import { useQueryClient } from '@tanstack/react-query';
import { useI18n } from '@monorepo/i18n';
import { DocumentsList } from '@monorepo/common';
import { BUCKET_TYPES } from '@monorepo/files';
import { Button, BUTTON_INTENT, Modal } from '@monorepo/ui-kit';
import useUser from '../../hooks/useUser';
import AccordionGrid from '../../common/AccordionGrid';
import useAddUserFileFO from '../../hooks/useAddUserFileFO';
import useDeleteUserFileFO from '../../hooks/useDeleteUserFileFO';
import styles from './index.module.css';

type TDocument = Document.AsObject;

const UserDocumentsFO = () => {
  const ref = useRef(null);
  const { data, isLoading: isLoadingUser } = useUser();
  const [file, setFile] = useState<TDocument>();
  const { successNotification, errorNotification } = useNotifications();
  const [inputFieldName, setInputFieldName] = useState(null);
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const { mutate: addUserFile, isLoading: isLoadingAdd } = useAddUserFileFO();
  const { mutate: deleteUserFile } = useDeleteUserFileFO();

  const handleOpen = (item: TDocument) => {
    if (ref?.current) {
      ref.current.setIsShow(true);
    }
    setFile(item);
  };

  const handleClose = () => {
    if (ref.current) {
      ref.current.setIsShow(false);
    }
  };

  const handleDelete = () => {
    if (ref.current) {
      ref.current.setIsShow(false);
    }
    // @ts-ignore
    deleteUserFile({ item: file });
  };

  const handleAdd = (item: TDocument) => {
    let documentType = 0;
    if (inputFieldName === 'legal') {
      documentType = 1;
    }
    if (inputFieldName === 'certificates') {
      documentType = 2;
    }
    const fileItem = { ...item, userId: data?.id, type: documentType };

    addUserFile(
      // @ts-ignore
      { item: fileItem },
      {
        onSuccess: () => {
          successNotification(t('updateUser.user.success'));
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USER] });
        },
        onError: (error: TGRPCPromiseReject) =>
          errorNotification(t(`updateUser.error.${error.status}`))
      }
    );
    setInputFieldName(null);
  };

  const isLoading = isLoadingAdd || isLoadingUser;

  const legalFiles = data
    ? data.documentsList.filter(
        (fileItem) => fileItem.type === Document.Type.LEGAL
      )
    : [];

  const financialFiles = data
    ? data?.documentsList.filter(
        (fileItem) => fileItem.type === Document.Type.FINANCIAL
      )
    : [];

  const certificateFiles = data
    ? data?.documentsList.filter(
        (fileItem) => fileItem.type === Document.Type.CERTIFICATE
      )
    : [];

  if (data) {
    return (
      <>
        <AccordionGrid
          label={t('documents.title.legal')}
          isDefaultOpen
          name="legal"
          handleAdd={handleAdd}
          isLoading={isLoading}
          setInputFieldName={setInputFieldName}
          stylesHeader={styles.header}
        >
          <DocumentsList
            items={legalFiles}
            onRemove={handleOpen}
            bucket={BUCKET_TYPES.USER_FILES}
            isLoading={isLoading}
            className={styles.item}
          />
        </AccordionGrid>
        <AccordionGrid
          isDefaultOpen
          label={t('documents.title.financial')}
          name="financial"
          handleAdd={handleAdd}
          isLoading={isLoading}
          setInputFieldName={setInputFieldName}
          stylesHeader={styles.header}
        >
          <DocumentsList
            items={financialFiles}
            onRemove={handleOpen}
            bucket={BUCKET_TYPES.USER_FILES}
            isLoading={isLoading}
            className={styles.item}
          />
        </AccordionGrid>
        <AccordionGrid
          isDefaultOpen
          label={t('documents.title.certificates')}
          name="certificates"
          handleAdd={handleAdd}
          isLoading={isLoading}
          setInputFieldName={setInputFieldName}
          stylesHeader={styles.header}
        >
          <DocumentsList
            items={certificateFiles}
            onRemove={handleOpen}
            bucket={BUCKET_TYPES.USER_FILES}
            isLoading={isLoading}
            className={styles.item}
          />
        </AccordionGrid>
        <Modal ref={ref}>
          <div className={styles.modal}>
            {t('delete.file.confirmation.title')}
            <div className={styles.buttonWrapper}>
              <Button
                className={styles.buttonReset}
                intent={BUTTON_INTENT.tertiary}
                onClick={() => handleClose()}
              >
                {t('personalInfo.form.cancel')}
              </Button>
              <Button
                intent={BUTTON_INTENT.primary}
                onClick={() => handleDelete()}
              >
                {t('personalInfo.form.confirm')}
              </Button>
            </div>
          </div>
        </Modal>
      </>
    );
  }

  return null;
};

export default UserDocumentsFO;
